













import { computed, defineComponent, ref, PropType } from '@vue/composition-api';
import { ErrorCircleIcon, StopCircleIcon, CheckCircleFilledIcon } from 'tdesign-icons-vue';
import dayjs from 'dayjs';
import i18n, { i18n_t } from '@/locales/i18n';
import {
  CoSyncRunningSubStatus,
  CoSyncStatus,
  CoSynStatusInfo,
  getErrorText,
  SyncStatusResultInfo,
  SyncStatusRunningInfo,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncEvent';
import { CoSyncInfo } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';

export default defineComponent({
  name: 'CoSyncListItemStatus',
  props: {
    statusInfo: {
      required: true,
      type: Object as PropType<CoSynStatusInfo>,
    },
    coSyncInfo: {
      required: true,
      type: Object as PropType<CoSyncInfo>,
    },
  },
  components: {
    ErrorCircleIcon,
    StopCircleIcon,
    CheckCircleFilledIcon,
  },
  setup(props, context) {
    const statusItem = computed(() => {
      const { statusInfo } = props;
      const info = {
        componentName: '',
        wording: '',
        tooltip: '',
        wordingClass: '',
        iconClass: '',
      };

      const getRunningWording = (statusInfo: SyncStatusRunningInfo) => {
        switch (statusInfo.subStatus) {
          case CoSyncRunningSubStatus.STATUS_RUNNING_SCANNER_START:
          case CoSyncRunningSubStatus.STATUS_RUNNING_PLANNER_START:
            return i18n_t('扫描中');
          case CoSyncRunningSubStatus.STATUS_RUNNING_OPERATE_START:
            return i18n_t('同步中，点击查看详情');
          default: {
            const _exhaustiveCheck: never = statusInfo.subStatus;
            return _exhaustiveCheck;
          }
        }
      };
      switch (statusInfo.status) {
        case CoSyncStatus.STATUS_IDLE: {
          Object.assign(info, {
            componentName: '',
            wording: i18n_t('未开始'),
            wordingClass: 'wording__idel',
            iconClass: 'icon__idel',
          });
          break;
        }
        case CoSyncStatus.STATUS_WAIT_FOR_RUNNING: {
          Object.assign(info, {
            componentName: 't-loading',
            wording: i18n_t('启动中'),
            wordingClass: 'wording__running',
            iconClass: 'icon__running',
          });
          break;
        }
        case CoSyncStatus.STATUS_RUNNING: {
          Object.assign(info, {
            componentName: 't-loading',
            wording: getRunningWording(statusInfo as SyncStatusRunningInfo),
            wordingClass: 'wording__running',
            iconClass: 'icon__running',
          });
          break;
        }
        case CoSyncStatus.STATUS_STOP: {
          Object.assign(info, {
            componentName: 'StopCircleIcon',
            wording: i18n_t('已停止'),
            wordingClass: 'wording__stop',
            iconClass: 'icon__stop',
          });
          break;
        }
        case CoSyncStatus.STATUS_SUCCESS: {
          const { finishTime, errorTaskNum, skippedFiles } = statusInfo as SyncStatusResultInfo;
          const skippedCount = skippedFiles?.length ?? 0;
          const wording = skippedCount
            ? i18n_t('已同步，跳过{slot1}个文件 {slot2}', {
                slot1: skippedCount,
                slot2: dayjs(finishTime).format('YYYY.MM.DD HH:mm:ss'),
              })
            : i18n_t('已同步 {slot1}', { slot1: dayjs(finishTime).format('YYYY.MM.DD HH:mm:ss') });
          const finishTimeWording = i18n_t('已同步 {slot1}', {
            slot1: dayjs(finishTime).format('YYYY/MM/DD HH:mm:ss'),
          });

          const tooltipParts = [finishTimeWording];
          if (errorTaskNum) {
            tooltipParts.push(i18n_t('{errorTaskNum}个文件同步异常', { errorTaskNum }));
          }
          if (skippedCount) {
            tooltipParts.push(i18n_t('跳过{slot1}个文件', { slot1: skippedCount }));
          }
          Object.assign(info, {
            componentName: skippedCount ? 'ErrorCircleIcon' : 'CheckCircleFilledIcon',
            wording,
            tooltip: tooltipParts.join('  '),
            wordingClass: skippedCount ? 'wording__warn' : 'wording__success',
            iconClass: skippedCount ? 'icon__warn' : 'icon__success',
          });
          break;
        }
        case CoSyncStatus.STATUS_ERROR: {
          Object.assign(info, {
            componentName: 'ErrorCircleIcon',
            wording: getErrorText(statusInfo as SyncStatusResultInfo),
            tooltip: getErrorText(statusInfo as SyncStatusResultInfo),
            wordingClass: 'wording__error',
            iconClass: 'icon__error',
          });
          break;
        }
        case CoSyncStatus.STATUS_WARN: {
          Object.assign(info, {
            componentName: 'ErrorCircleIcon',
            wording: getErrorText(statusInfo as SyncStatusResultInfo),
            tooltip: getErrorText(statusInfo as SyncStatusResultInfo),
            wordingClass: 'wording__warn',
            iconClass: 'icon__warn',
          });
          break;
        }
        case CoSyncStatus.STATUS_CONFLICT: {
          Object.assign(info, {
            componentName: 'ErrorCircleIcon',
            wording: i18n_t('同步冲突'),
            tooltip: i18n_t('有文件同步存在冲突'),
            wordingClass: 'wording__conflict',
            iconClass: 'icon__conflict',
          });
          break;
        }

        default: {
          const _exhaustiveCheck: never = statusInfo.status;
          return _exhaustiveCheck;
        }
      }

      return info;
    });

    const isTooltipsEnable = computed(() => statusItem.value.tooltip.length > 0);

    const isTooltipsVisable = ref(false);

    const methods = {
      handleMouseEnter() {
        if (isTooltipsEnable.value) {
          isTooltipsVisable.value = true;
        }
      },
      handleMouseLeave() {
        isTooltipsVisable.value = false;
      },
      handleMouseClick() {
        context.emit('on-click', { coSyncInfo: props.coSyncInfo, statusInfo: props.statusInfo });
      },
    };

    return {
      statusItem,
      isTooltipsVisable,

      handleMouseEnter: methods.handleMouseEnter,
      handleMouseLeave: methods.handleMouseLeave,
      handleMouseClick: methods.handleMouseClick,

      tipsStyle: (trigger: HTMLElement) => ({ 'word-break': 'break-all' }),
    };
  },
});
